/**
 * Class T — the occasions that genuinely cannot be computed.
 *
 * ── What happened to the first version of this file ────────────────────────
 *
 * It shipped ten years of hand-typed dates behind a `VERIFIED = false` gate.
 * When they were finally checked against published sources, most were wrong:
 *
 *   Diwali         1 of 10 wrong  (2028 was Nov 15; it is Oct 17)
 *   Holi           1 of 10 wrong  (2032 was Feb 27; it is Mar 27 — a month out)
 *   Navratri       1 of 8 wrong   (2028 was Oct 18; it is Sep 19)
 *   Onam           8 of 10 wrong
 *   Loy Krathong  10 of 10 wrong  (every year 1–2 days early)
 *   Nowruz         2 of 10 wrong
 *
 * That is what the gate was for, and it is why the rule below is now the rule.
 *
 * ── The rule ───────────────────────────────────────────────────────────────
 *
 * A date is in this file ONLY if two independent checks agree on it. Every
 * occasion records which checks, and the last year they cover. Past
 * `verifiedThrough`, `tableDate` returns null, `resolveOccasion` returns null,
 * and the editor says "Dates need updating" — which is the correct behaviour,
 * because a wrong Diwali is worse than a missing one.
 *
 * The second check is ASTRONOMY wherever the festival has a lunar definition.
 * Diwali is Kartika Amavasya, Holi is the day after Phalguna Purnima, Raksha
 * Bandhan is Shravana Purnima, Navratri opens on Ashwin Shukla Pratipada — so
 * each published date must land on, or one day either side of, a real lunar
 * phase. `script/verify-calendar.mjs` recomputes that from Meeus on every
 * `npm run verify`, so a future edit that introduces a wrong date fails the
 * build rather than reaching a merchant.
 *
 * ── What is NOT here, and why ──────────────────────────────────────────────
 *
 * `nowruz` moved to algorithmic.ts. It is the March equinox measured at Tehran,
 * so it is exactly computable and needs no table, no sources and no horizon.
 *
 * `onam` was REMOVED from the shipped catalogue. Thiruvonam depends on the
 * Malayalam solar month and the Thiruvonam nakshatra, so there is no lunar-phase
 * check to corroborate it, and the two published sources found disagree on 2026
 * — the nearest and most important year. Offering a Kerala festival on the wrong
 * day to Kerala merchants is worse than not offering it. It comes back when
 * someone can verify it properly.
 */

export type LunarRule =
  | 'kartika-amavasya'
  | 'phalguna-purnima'
  | 'shravana-purnima'
  | 'ashwin-pratipada'
  | 'thai-month-12';

export interface TableEntry {
  /** The last year every date here is corroborated to. Lookups past it return null. */
  verifiedThrough: number;
  /** Published sources consulted, with the date they were retrieved. */
  sources: string[];
  /**
   * The astronomical rule `script/verify-calendar.mjs` checks these against.
   * Absent means no independent check exists — itself a reason to keep the
   * horizon short.
   */
  rule?: LunarRule;
  /** Gregorian `[month, day]` by year. */
  dates: Record<number, [number, number]>;
}

export const TABLES: Record<string, TableEntry> = {
  /**
   * Diwali — Lakshmi Puja, the main day, North Indian (purnimanta) reckoning.
   * Two published sources agree on every year, and all eight land on the day of,
   * or the day before, the computed Kartika new moon.
   */
  diwali: {
    verifiedThrough: 2033,
    rule: 'kartika-amavasya',
    sources: [
      'https://diwali.info/diwali-dates — retrieved 2026-08-26',
      'https://www.prokerala.com/when-is/diwali.html — retrieved 2026-08-26',
      'https://www.calendarlabs.com/holidays/india/diwali.php — retrieved 2026-08-26 (2026 only)',
    ],
    dates: {
      2026: [11, 8],
      2027: [10, 29],
      2028: [10, 17],
      2029: [11, 5],
      2030: [10, 26],
      2031: [11, 14],
      2032: [11, 2],
      2033: [10, 22],
    },
  },

  /**
   * Holi — Rangwali Holi, the day of colours.
   *
   * ⚠ Sources differ by a day on whether they list Rangwali Holi or the Holika
   * Dahan bonfire the evening before. The two used here agree with each other on
   * all eight years. The ±1 ambiguity is materially harmless in this product:
   * the occasion's default padding is two days before and one after, so the
   * window covers both readings either way.
   */
  holi: {
    verifiedThrough: 2033,
    rule: 'phalguna-purnima',
    sources: [
      'https://www.prokerala.com/when-is/holi.html — retrieved 2026-08-26',
      'https://www.qppstudio.net/global-holidays-observances/holi-phagwa.htm — retrieved 2026-08-26',
    ],
    dates: {
      2026: [3, 3],
      2027: [3, 22],
      2028: [3, 11],
      2029: [3, 1],
      2030: [3, 20],
      2031: [3, 9],
      2032: [3, 27],
      2033: [3, 16],
    },
  },

  /** Raksha Bandhan — Shravana Purnima. */
  'raksha-bandhan': {
    verifiedThrough: 2033,
    rule: 'shravana-purnima',
    sources: [
      'https://www.prokerala.com/when-is/raksha-bandhan.html — retrieved 2026-08-26',
      'https://www.calendarlabs.com/holidays/india/raksha-bandhan.php — retrieved 2026-08-26 (2026 only)',
    ],
    dates: {
      2026: [8, 28],
      2027: [8, 17],
      2028: [8, 5],
      2029: [8, 24],
      2030: [8, 13],
      2031: [8, 2],
      2032: [8, 20],
      2033: [8, 10],
    },
  },

  /**
   * Navratri — first day of Sharada (autumn) Navratri, Ashwin Shukla Pratipada.
   *
   * Only one published source was reachable, so the astronomical check does the
   * corroborating work here — and it earned it: it is what showed the old
   * table's 2028 (Oct 18) to be impossible and the source's Sep 19 to be right.
   */
  navratri: {
    verifiedThrough: 2033,
    rule: 'ashwin-pratipada',
    sources: ['https://www.prokerala.com/when-is/navratri.html — retrieved 2026-08-26'],
    dates: {
      2026: [10, 11],
      2027: [9, 30],
      2028: [9, 19],
      2029: [10, 8],
      2030: [9, 28],
      2031: [10, 17],
      2032: [10, 5],
      2033: [9, 24],
    },
  },

  /**
   * Loy Krathong — 15th waxing of the 12th Thai lunar month.
   *
   * ⚠ SHORTEST HORIZON, deliberately. The Thai lunar calendar is TABULAR, so it
   * drifts from the real moon by design and the astronomical check can only
   * confirm "within a day" rather than pin the date. Thailand's own TAT confirms
   * festival dates about six weeks ahead, so anything further out is provisional
   * even when correctly derived.
   */
  'loy-krathong': {
    verifiedThrough: 2030,
    rule: 'thai-month-12',
    sources: [
      'https://www.umetravel.com/loy-krathong-festival/dates.html — retrieved 2026-08-26',
      'https://www.thailandhighlights.com/thailand/loy-krathong-festival — retrieved 2026-08-26',
    ],
    dates: { 2026: [11, 25], 2027: [11, 14], 2028: [11, 2], 2029: [11, 21], 2030: [11, 11] },
  },
};

/**
 * Regional variants, keyed `<id>@<variant>`.
 *
 * Empty. The old file carried a `diwali@south` table (Naraka Chaturdashi, the
 * day before the northern Lakshmi Puja) that no source was ever found for. The
 * distinction is real and worth shipping — South India does observe Deepavali a
 * day earlier — but not on invented dates.
 */
export const VARIANTS: Record<string, TableEntry> = {};

/** The last year ANY table covers. */
export const TABLE_END_YEAR = Math.max(...Object.values(TABLES).map((t) => t.verifiedThrough));

/** A table lookup. Returns null past the occasion's own verified horizon. */
export function tableDate(id: string, y: number, variant?: string): [number, number] | null {
  if (variant) {
    const v = VARIANTS[`${id}@${variant}`];
    if (v) return y <= v.verifiedThrough ? (v.dates[y] ?? null) : null;
  }
  const t = TABLES[id];
  if (!t || y > t.verifiedThrough) return null;
  return t.dates[y] ?? null;
}

export interface CalendarVerification {
  ok: boolean;
  message: string;
}

/**
 * Is every shipped Class T occasion backed by a source and a horizon?
 *
 * A STRUCTURAL check: it asserts that the provenance exists, not that the dates
 * are right. The dates themselves are checked against astronomy by
 * `script/verify-calendar.mjs`, which runs inside `npm run verify`.
 */
export function checkCalendarVerified(): CalendarVerification {
  const problems: string[] = [];
  for (const [id, t] of Object.entries(TABLES)) {
    if (!t.sources.length) problems.push(`${id}: no sources recorded`);
    if (!t.verifiedThrough) problems.push(`${id}: no verifiedThrough`);
    const years = Object.keys(t.dates).map(Number);
    if (!years.length) problems.push(`${id}: no dates`);
    const beyond = years.filter((y) => y > t.verifiedThrough);
    if (beyond.length) {
      problems.push(
        `${id}: carries ${beyond.join(', ')} beyond its verified horizon ${t.verifiedThrough}`
      );
    }
  }
  return problems.length
    ? { ok: false, message: `calendar: ${problems.join('; ')}` }
    : {
        ok: true,
        message: `calendar: ${Object.keys(TABLES).length} Class T occasions, each sourced and horizon-bounded`,
      };
}
